import { apiUrl } from "../api/endpoints";
import React, { useState, useContext, useEffect } from 'react';
import '../Components/modal.css';
import './edit.css';
import './DownloadReportShared.css';
import axios from 'axios';

import Select from 'react-select';
import Alert from '../Components/Alert';
import { LanguageContext } from '../context/LanguageContext';
import devTranslations from '../languages/template.json';
import webDashboardTranslations from '../languages/webDashboard.json';
import { getErrorMessage } from '../utils/errorUtils';
import { useAuth } from '../hooks/AuthProvider';
import { useFontSize } from '../context/FontSizeContext';
import WebDashboardLayoutModeField from '../Components/WebDashboardLayoutModeField';
import WorkDaysHoursPicker from '../Components/WorkDaysHoursPicker';
import AiReportTypeField from '../Components/AiReportTypeField';
import { useAiReportTypes } from '../utils/aiReportTypes';
import {
    MAP_ICON_RULES,
    deleteStoredMapIcon,
    uploadTemplateMapIcon,
    validateMapIconFile,
} from '../utils/mapIconUpload';
import {
    createDefaultWebDashboardTab,
    WEB_DASHBOARD_LAYOUT_MODES,
} from '../utils/webDashboardLayout';

import { TemplateIcon } from '../svg/TemplateIcons';
import { NameIcon, AddNewIcon } from '../svg/UtilsIcons';
import { DeviceIdIcon } from '../svg/DeviceIcons';

const hardwareOptions = [
    'Arduino',
    'BBC Micro:bit',
    'ESP32',
    'ESP8266',
    'Lantronix Fox 3',
    'Microduino',
    'Onion Omega',
    'Particle',
    'Pycom WiPy',
    'Raspberry Pi',
    'Seeed Wio Terminal',
    'Teensy',
    'TinyDuino',
    'Other'
];

const connectionTypeOptions = ['Ethernet', 'WiFi', 'Satellite', 'GSM'];

const TID_PREFIX = 'TMPL';

const stripTidPrefix = (value) => {
    const raw = String(value ?? '').replace(/\s+/g, '');
    if (!raw) return '';

    const upper = raw.toUpperCase();
    if (upper.startsWith('TMPL-')) return raw.slice(5);
    if (upper.startsWith('TMPL')) return raw.slice(4);
    return raw;
};

const toApiTid = (userTid) => {
    const suffix = stripTidPrefix(userTid);
    return suffix ? `${TID_PREFIX}${suffix}` : '';
};

export const AddTemplate = ({
    bg,
    fg,
    ft,
    textColor,
    btnPrimary,
    btnSecondary,
    setOpen,
    refresh
}) => {
    const { permissions } = useAuth();
    const { lang } = useContext(LanguageContext);
    useFontSize();

    const canAddNewTemplates = permissions['add_new_templates'];
    const t = devTranslations[lang].addTemplate;
    const webDashboardT = webDashboardTranslations[lang]?.webDashboard || {};
    const isRtl = ['ar', 'ur'].includes(lang);

    const [creationMode, setCreationMode] = useState('blank'); 
    const { options: aiReportTypeOptions, availability: aiReportAvailability } = useAiReportTypes();
    const showAiReportTypeField = aiReportAvailability.templates !== false;
    const [examples, setExamples] = useState([]);
    const [loadingExamples, setLoadingExamples] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [mapIconFile, setMapIconFile] = useState(null);
    const [mapIconPreviewUrl, setMapIconPreviewUrl] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        tid: '',
        description: '',
        type: null,
        hardware: null,
        connectionType: null,
        template_example_id: null,
        initialTabLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
        work_days_hours: [],
    });

    const [alertState, setAlertState] = useState({
        open: false,
        type: 'success',
        message: '',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = (type, message) => {
        setAlertState({
            open: true,
            type,
            message,
            position: isRtl ? 'left' : 'right'
        });
    };

    const handleAlertClose = () => {
        setAlertState(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {
        const fetchExamples = async () => {
            if (!canAddNewTemplates) return;
            if (creationMode !== 'example' || examples.length > 0) return;

            setLoadingExamples(true);
            try {
                const res = await axios.get(
                    apiUrl(`template/examples/list`)
                );
                if (res.data && res.data.status === 'OK') {
                    setExamples(
                        res.data.data.map(ex => ({
                            value: ex.template_example_id,
                            label: ex.example_name,
                            ...ex
                        }))
                    );
                } else {
                    throw new Error('Invalid examples response');
                }
            } catch (err) {
                console.error('Failed to load examples', err);
                showAlert('error', t.loadExamplesError || 'Failed to load template examples.');
            } finally {
                setLoadingExamples(false);
            }
        };

        fetchExamples();
    }, [creationMode, canAddNewTemplates, examples.length]);

    const handleModeSwitch = mode => {
        setCreationMode(mode);
        if (mapIconPreviewUrl) {
            URL.revokeObjectURL(mapIconPreviewUrl);
        }
        setMapIconFile(null);
        setMapIconPreviewUrl('');
        setFormData({
            name: '',
            tid: '',
            description: '',
            type: null,
            hardware: null,
            connectionType: null,
            template_example_id: null,
            initialTabLayoutMode: WEB_DASHBOARD_LAYOUT_MODES.PIXELS,
            work_days_hours: [],
        });
    };

    useEffect(() => {
        return () => {
            if (mapIconPreviewUrl) {
                URL.revokeObjectURL(mapIconPreviewUrl);
            }
        };
    }, [mapIconPreviewUrl]);

    const onChange = e => {
        const { name, value } = e.target;
        const clean = name === 'tid' ? stripTidPrefix(value) : value;
        setFormData(prev => ({ ...prev, [name]: clean }));
    };

    const handleSelectChange = (key, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [key]: selectedOption ? selectedOption.value : null
        }));
    };

    const handleExampleChange = selectedOption => {
        if (selectedOption) {
            setFormData(prev => ({
                ...prev,
                template_example_id: selectedOption.value,
                description: selectedOption.description || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                template_example_id: null,
                description: ''
            }));
        }
    };

    const handleMapIconChange = async event => {
        const file = event.target.files?.[0] || null;
        event.target.value = '';

        if (!file) return;

        try {
            await validateMapIconFile(file);
            if (mapIconPreviewUrl) {
                URL.revokeObjectURL(mapIconPreviewUrl);
            }
            setMapIconFile(file);
            setMapIconPreviewUrl(URL.createObjectURL(file));
        } catch (err) {
            showAlert('error', err.message || 'Invalid map icon file.');
        }
    };

    const clearMapIcon = () => {
        if (mapIconPreviewUrl) {
            URL.revokeObjectURL(mapIconPreviewUrl);
        }
        setMapIconFile(null);
        setMapIconPreviewUrl('');
    };

    const createTemplate = async () => {
        if (submitting) return;

        let uploadedMapIcon = null;
        try {
            if (!formData.name.trim()) {
                showAlert(
                    'error',
                    t.validation?.nameRequired || 'Name is required.'
                );
                return;
            }
            if (!String(formData.tid ?? '').trim()) {
                showAlert(
                    'error',
                    t.validation?.tidRequired ||
                        'Template ID (TID) is required.'
                );
                return;
            }

            if (creationMode === 'example') {
                if (!formData.template_example_id) {
                    showAlert('error', t.validation?.exampleRequired || 'Please select an example.');
                    return;
                }
            }

            setSubmitting(true);
            const payload = {
                name: formData.name.trim(),
                tid: toApiTid(formData.tid),
                description: formData.description,
                type: formData.type,
                work_days_hours: formData.work_days_hours,
            };
            if (mapIconFile) {
                uploadedMapIcon = await uploadTemplateMapIcon(mapIconFile, formData.name);
                payload.map_icon = uploadedMapIcon;
            }

            if (creationMode === 'example') {
                payload.create_example = true;
                payload.template_example_id = formData.template_example_id;
            } else {
                payload.hardware = formData.hardware;
                payload.connectionType = formData.connectionType;
                payload.webdashboard = [
                    createDefaultWebDashboardTab({
                        name: 'Dashboard',
                        layoutMode: formData.initialTabLayoutMode,
                    }),
                ];
            }

            const res = await axios.post(
                apiUrl(`template/create`),
                payload,
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (res.status === 201 && res.data.status === 'OK') {
                showAlert(
                    'success',
                    res.data.message || t.createSuccess || 'Template created successfully.'
                );
                refresh();
                setTimeout(() => {
                    setOpen(false);
                }, 2000);
            } else {
                throw new Error(t.unexpectedResponse || 'Received an unexpected response from the server.');
            }
        } catch (err) {
            console.error('Create Template Error:', err);
            if (uploadedMapIcon) {
                try {
                    await deleteStoredMapIcon(uploadedMapIcon);
                } catch (cleanupErr) {
                    console.warn('Failed to clean up uploaded map icon after template create failed.', cleanupErr);
                }
            }
            const errorMessage = getErrorMessage(err);
            showAlert('error', errorMessage);
            setSubmitting(false);
        }
    };

    if (!canAddNewTemplates) {
        return (
            <div
                id="modal"
                style={{ direction: isRtl ? 'rtl' : 'ltr' }}
            >
                <div
                    id="popup"
                    className="dashboard-modal"
                    style={{
                        animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                        fontSize:
                            'calc(var(--body-font-size) * var(--font-size-multiplier))'
                    }}
                >
                    <div className="dashboard-header">
                        <div className="dashboard-header-icon">
                            <AddNewIcon />
                        </div>
                        <div className="dashboard-header-text">
                            <h2>
                                {t.permissionDenied?.title ||
                                    'Permission Denied'}
                            </h2>
                            <p>
                                {t.permissionDenied?.message ||
                                    'You do not have permission to add new templates.'}
                            </p>
                        </div>
                    </div>

                    <div className="modal-divider" />

                    <div className="modal-content dashboard-content" />

                    <div className="modal-footer dashboard-footer">
                        <button
                            className="btn btn-cancel"
                            onClick={() => setOpen(false)}
                        >
                            {t.buttonClose || 'Close'}
                        </button>
                        <div />
                    </div>
                </div>
            </div>
        );
    }

    const hardwareSelectOptions = hardwareOptions.map(option => ({
        value: option,
        label: option
    }));
    const connectionTypeSelectOptions = connectionTypeOptions.map(option => ({
        value: option,
        label: option
    }));

    const selectedHardwareOption = hardwareSelectOptions.find(
        option => option.value === formData.hardware
    );
    const selectedConnectionTypeOption = connectionTypeSelectOptions.find(
        option => option.value === formData.connectionType
    );
    const selectedExampleOption = examples.find(
        option => option.value === formData.template_example_id
    );

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: 'var(--rs-control-bg)',
    borderColor: state.isFocused
      ? 'var(--rs-control-border-focus)'
      : 'var(--rs-control-border)',
    boxShadow: state.isFocused ? 'var(--rs-control-focus-shadow)' : 'none',
    '&:hover': {
      borderColor: 'var(--rs-control-border-hover)',
    },
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    minHeight: '44px',
    borderRadius: 8,
    cursor: 'pointer',
  }),

  singleValue: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  input: (provided) => ({
    ...provided,
    color: 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  placeholder: (provided) => ({
    ...provided,
    color: 'var(--rs-placeholder)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--rs-menu-bg)',
    border: '1px solid var(--rs-menu-border)',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 9999,
  }),

  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--rs-option-selected-bg)'
      : state.isFocused
      ? 'var(--rs-option-hover-bg)'
      : 'var(--rs-option-bg)',
    color: state.isSelected ? 'var(--rs-option-selected-text)' : 'var(--rs-text)',
    fontSize: 'calc(var(--body-font-size) * var(--font-size-multiplier))',
    '&:active': {
      backgroundColor: 'var(--rs-option-active-bg)',
    },
    cursor: 'pointer',
  }),

  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    paddingRight: 12,
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  clearIndicator: (provided) => ({
    ...provided,
    color: 'var(--rs-indicator)',
    '&:hover': {
      color: 'var(--rs-indicator-hover)',
    },
  }),

  indicatorSeparator: () => ({
    display: 'none',
  }),
};
    return (
        <div
            id="modal"
            style={{ direction: isRtl ? 'rtl' : 'ltr' }}
        >
            <Alert
                type={alertState.type}
                position={alertState.position}
                message={alertState.message}
                open={alertState.open}
                close={handleAlertClose}
            />

            <div
                id="popup"
                className="dashboard-modal"
                style={{
                    animationName: isRtl ? 'reverse-add-zoom' : 'add-zoom',
                    fontSize:
                        'calc(var(--body-font-size) * var(--font-size-multiplier))'
                }}
            >
                <div className="dashboard-header">
                    <div className="dashboard-header-icon">
                        <TemplateIcon />
                    </div>
                    <div className="dashboard-header-text">
                        <h2>{t.title || 'New Template'}</h2>
                        <p>
                            {t.description ||
                                'Create new Template by filling in the form below'}
                        </p>
                    </div>
                </div>

                <div className="modal-divider" />

                <div className="modal-content dashboard-content">
    <div className="form-section template-mode-section">
        <div className="template-mode-buttons">
            <button
                type="button"
                className={`btn btn-mode-template ${
                    creationMode === 'blank'
                        ? 'btn-mode-template-active'
                        : 'btn-mode-template-inactive'
                }`}
                onClick={() => handleModeSwitch('blank')}
            >
                {t.modeBlank || 'Blank Template'}
            </button>
            <button
                type="button"
                className={`btn btn-mode-template ${
                    creationMode === 'example'
                        ? 'btn-mode-template-active'
                        : 'btn-mode-template-inactive'
                }`}
                onClick={() => handleModeSwitch('example')}
            >
                {t.modeExample || 'From Example'}
            </button>
        </div>
    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <NameIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelName || 'Dashboard Name'}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter">
                                <input
                                    name="name"
                                    type="text"
                                    className="add-column"
                                    placeholder={t.placeholderName || 'Name'}
                                    maxLength={50}
                                    value={formData.name}
                                    onChange={onChange}
                                />
                                <span className="field-counter">
                                    {formData.name?.length || 0} / 50
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <DeviceIdIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelTid || 'Template ID'}
                            </span>
                        </div>

                        <div className="form-item">
                            <div
                                className="add-column"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '0 12px'
                                }}
                            >
                                <span
                                    style={{
                                        fontWeight: 700,
                                        opacity: 0.85,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {TID_PREFIX}
                                </span>

                                <input
                                    name="tid"
                                    type="text"
                                    className="add-column"
                                    placeholder={
                                        t.placeholderTid || 'Enter Template id'
                                    }
                                    maxLength={45}
                                    value={formData.tid}
                                    onChange={onChange}
                                    onKeyDown={e => {
                                        if (e.key === ' ') e.preventDefault();
                                    }}
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        padding: 0,
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {creationMode === 'example' && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <TemplateIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {t.labelExample || 'Template Example'}
                                </span>
                            </div>

                            <div className="form-item">
                                <Select
                                    id="template-example"
                                    options={examples}
                                    value={selectedExampleOption || null}
                                    onChange={handleExampleChange}
                                    placeholder={
                                        loadingExamples
                                            ? t.loadingExamples ||
                                              'Loading examples...'
                                            : t.placeholderExample ||
                                              'Select an Example'
                                    }
                                    isClearable
                                    isDisabled={loadingExamples}
                                    styles={customStyles}
                                />
                                {selectedExampleOption && (
                                    <small className="template-example-meta">
                                        {`Hardware: ${
                                            selectedExampleOption.hardware
                                        } | Conn: ${
                                            selectedExampleOption.connectionType
                                        }`}
                                    </small>
                                )}
                            </div>
                        </div>
                    )}

                    {creationMode === 'blank' && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <TemplateIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {webDashboardT.layoutModeLabel || 'Layout Mode'}
                                </span>
                            </div>

                            <div className="form-item">
                                <WebDashboardLayoutModeField
                                    value={formData.initialTabLayoutMode}
                                    onChange={(nextMode) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            initialTabLayoutMode: nextMode,
                                        }))
                                    }
                                    title={webDashboardT.layoutModeLabel || 'Layout Mode'}
                                    helperText={
                                        webDashboardT.layoutModeHelper ||
                                        'Choose how widget sizes and positions are stored for this tab.'
                                    }
                                    translations={webDashboardT}
                                />
                            </div>
                        </div>
                    )}

                    {creationMode === 'blank' && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <TemplateIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {t.labelHardware || 'Hardware'}
                                </span>
                            </div>

                            <div className="form-item">
                                <Select
                                    id="hardware"
                                    options={hardwareSelectOptions}
                                    value={selectedHardwareOption || null}
                                    onChange={selectedOption =>
                                        handleSelectChange(
                                            'hardware',
                                            selectedOption
                                        )
                                    }
                                    styles={customStyles}
                                    placeholder={
                                        t.placeholderHardware ||
                                        'Select Hardware'
                                    }
                                    isClearable
                                />
                            </div>
                        </div>
                    )}

                    {creationMode === 'blank' && (
                        <div className="form-section">
                            <div className="form-section-title">
                                <span className="form-section-icon">
                                    <TemplateIcon />
                                </span>
                                <span className="form-section-title-text">
                                    {t.labelConnectionType ||
                                        'Connection Type'}
                                </span>
                            </div>

                            <div className="form-item">
                                <Select
                                    id="connectionType"
                                    options={connectionTypeSelectOptions}
                                    value={selectedConnectionTypeOption || null}
                                    onChange={selectedOption =>
                                        handleSelectChange(
                                            'connectionType',
                                            selectedOption
                                        )
                                    }
                                    styles={customStyles}
                                    placeholder={
                                        t.placeholderConnectionType ||
                                        'Select Connection Type'
                                    }
                                    isClearable
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <TemplateIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelMapIcon || 'Map icon'}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="template-map-icon-uploader">
                                <label className="template-map-icon-drop">
                                    <input
                                        type="file"
                                        accept={MAP_ICON_RULES.accept}
                                        onChange={handleMapIconChange}
                                    />
                                    {mapIconPreviewUrl ? (
                                        <img src={mapIconPreviewUrl} alt="" className="template-map-icon-preview" />
                                    ) : (
                                        <span>{t.uploadMapIcon || 'Upload map icon'}</span>
                                    )}
                                </label>
                                <div className="template-map-icon-meta">
                                    <div>{t.mapIconRules || 'PNG, JPG, WebP, or SVG. 24-512px. Max 512KB.'}</div>
                                    {mapIconFile ? (
                                        <button type="button" className="btn btn-cancel" onClick={clearMapIcon}>
                                            {t.removeMapIcon || 'Remove'}
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    {showAiReportTypeField && (
                        <AiReportTypeField
                            value={formData.type}
                            onChange={value => setFormData(prev => ({ ...prev, type: value }))}
                            options={aiReportTypeOptions}
                            styles={customStyles}
                            label="AI Report Type"
                            placeholder="Select AI report type"
                        />
                    )}

                    <div className="form-section">
                        <div className="form-section-title">
                            <span className="form-section-icon">
                                <TemplateIcon />
                            </span>
                            <span className="form-section-title-text">
                                {t.labelDesc || 'Description'}
                            </span>
                        </div>

                        <div className="form-item">
                            <div className="field-with-counter field-with-counter--textarea">
                                <textarea
                                    className="add-column"
                                    name="description"
                                    rows={5}
                                    maxLength={128}
                                    placeholder={
                                        t.placeholderDesc || 'Description'
                                    }
                                    value={formData.description}
                                    onChange={onChange}
                                    style={{ resize: 'none', height: '140px' }}
                                />
                                <span className="field-counter">
                                    {formData.description?.length || 0} / 128
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <WorkDaysHoursPicker
                            value={formData.work_days_hours}
                            onChange={(nextRows) =>
                                setFormData((prev) => ({ ...prev, work_days_hours: nextRows }))
                            }
                            labels={{
                                title: t.workDaysHoursTitle || 'Work days and hours',
                                sameShift: t.sameShift || 'Use same shift start / shift end for all work days',
                            }}
                        />
                    </div>
                </div>

                <div className="modal-footer dashboard-footer">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setOpen(false)}
                        disabled={submitting}
                    >
                        {t.buttonCancel || 'Cancel'}
                    </button>
                    <button
                        className="btn btn-apply dashboard-primary-btn"
                        onClick={createTemplate}
                        disabled={submitting}
                    >
                        {t.buttonDone || 'Save template'}
                    </button>
                </div>
            </div>
        </div>
    );
};
